import { Box, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { motion, AnimatePresence, MotionConfig, useReducedMotion } from "framer-motion";
import Login from "../components/Authentication/Login";
import Signup from "../components/Authentication/Signup";
import "./AuthPremium.css";

const ROTATING = ["fast.", "loud.", "together.", "fearless."];

function useRotatingWord() {
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  useEffect(() => {
    // Reduced-motion users get the static first word — no interval.
    if (reduceMotion) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % ROTATING.length), 2200);
    return () => clearInterval(id);
  }, [reduceMotion]);
  return ROTATING[index];
}

function Homepage() {
  const history = useHistory();
  const [tab, setTab] = useState(0);
  const [mobileAuth, setMobileAuth] = useState(false);
  const word = useRotatingWord();

  const goAuth = (t) => {
    setTab(t);
    setMobileAuth(true);
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem("userInfo");
      if (!stored) return;
      const user = JSON.parse(stored);
      if (user) history.push("/chats");
    } catch {
      localStorage.removeItem("userInfo");
    }
  }, [history]);

  return (
    <MotionConfig reducedMotion="user">
    <Box className="auth-stage" minH="100vh" w="100%">
      <div className="aurora aurora-a" />
      <div className="aurora aurora-b" />
      <div className="aurora aurora-c" />

      <div className="auth-inner">
        <div className={`auth-track${mobileAuth ? " show-auth" : ""}`}>
        {/* ============ LEFT : HERO ============ */}
        <motion.div
          className="hero-block"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="brand-row">
            <div className="logo-mark">M</div>
            <div>
              <div className="brand-name">Meet-Up</div>
              <div className="brand-sub">Realtime Chat OS</div>
            </div>
          </div>

          <div className="live-badge">
            <span className="live-dot">
              <i />
            </span>
            12,480 online now &nbsp;•&nbsp; v2.0 live
          </div>

          <h1 className="hero-title">
            <span className="stroke">Talk</span> <span className="gradient-word">fast.</span>
            <br />
            Vibe{" "}
            <span className="rotator">
              <AnimatePresence exitBeforeEnter>
                <motion.span
                  key={word}
                  className="gradient-word"
                  style={{ display: "inline-block" }}
                  initial={{ rotateY: 90, opacity: 0 }}
                  animate={{ rotateY: 0, opacity: 1 }}
                  exit={{ rotateY: -90, opacity: 0 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                >
                  {word}
                </motion.span>
              </AnimatePresence>
            </span>
          </h1>

          <p className="hero-copy">
            Meet-Up is the <b>aggressive, elegant</b> way to message — group rooms,
            instant search, and <b>sub-50ms delivery</b>. No noise. Just pure,
            electric conversation.
          </p>

          <div className="feature-grid">
            {[
              { icon: "⚡", label: "<50ms delivery" },
              { icon: "🔒", label: "Private & secure" },
              { icon: "👥", label: "Groups + 1:1" },
              { icon: "🔍", label: "Instant search" },
            ].map((f) => (
              <div key={f.label} className="feature-chip">
                <span className="ic">{f.icon}</span>
                {f.label}
              </div>
            ))}
          </div>

          <div className="mobile-cta-row">
            <button type="button" className="mcta mcta-primary" onClick={() => goAuth(0)}>
              Login →
            </button>
            <button type="button" className="mcta mcta-ghost" onClick={() => goAuth(1)}>
              Sign Up
            </button>
          </div>

          <div className="mock-wrap">
            <div className="mock-card mock-a">
              <div className="mock-head">
                <div className="mock-avatar" style={{ background: "linear-gradient(135deg,#6e3aff,#a855f7)" }}>
                  S
                </div>
                <span className="mock-name">Sarah ⚡</span>
                <span className="mock-time">now</span>
              </div>
              <div className="mock-text">Shipped the new room in 40ms. This thing FLIES 🚀</div>
            </div>
            <div className="mock-card mock-b">
              <div className="mock-head">
                <div className="mock-avatar" style={{ background: "linear-gradient(135deg,#00e5ff,#0ea5e9)" }}>
                  U
                </div>
                <span className="mock-name">You</span>
                <span className="mock-time">
                  typing
                  <span className="typing-dots">
                    <i />
                    <i />
                    <i />
                  </span>
                </span>
              </div>
              <div className="mock-text">Say less. Meet-Up or nothing.</div>
            </div>
          </div>

          <div className="stats-row">
            {[
              { value: "50ms", label: "Median latency" },
              { value: "99.99%", label: "Uptime" },
              { value: "120k+", label: "Msgs / day" },
            ].map((s) => (
              <div className="stat" key={s.label}>
                <b>{s.value}</b>
                <span>{s.label}</span>
              </div>
            ))}
          </div>

          <div className="social-proof">
            <div className="avatar-stack">
              <div style={{ background: "linear-gradient(135deg,#f472b6,#a855f7)" }}>AK</div>
              <div style={{ background: "linear-gradient(135deg,#22d3ee,#6366f1)" }}>JM</div>
              <div style={{ background: "linear-gradient(135deg,#f59e0b,#ef4444)" }}>RS</div>
              <div style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)" }}>+9k</div>
            </div>
            <p>
              Loved by <b>9,000+ night owls</b>, builders & group-chat legends.
            </p>
          </div>
        </motion.div>

        {/* ============ RIGHT : AUTH CARD ============ */}
        <motion.div
          className="auth-card-outer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <button type="button" className="mobile-back" onClick={() => setMobileAuth(false)}>
            ← Back
          </button>
          <div className="auth-card">
            <div className="auth-eyebrow">● Secure Access</div>
            <h2 className="auth-heading">{tab === 0 ? "Welcome back, legend." : "Join the loud side."}</h2>
            <p className="auth-sub">
              {tab === 0
                ? "Log in to pick up exactly where the conversation exploded."
                : "Create your account in 20 seconds. Free forever. No mercy for boring chats."}
            </p>

            <div className="tab-switch" role="tablist" aria-label="Authentication mode">
              <div className="tab-glider" style={{ left: tab === 0 ? "5px" : "50%" }} />
              <button
                className={`tab-btn ${tab === 0 ? "active" : ""}`}
                role="tab"
                aria-selected={tab === 0}
                onClick={() => setTab(0)}
              >
                Login
              </button>
              <button
                className={`tab-btn ${tab === 1 ? "active" : ""}`}
                role="tab"
                aria-selected={tab === 1}
                onClick={() => setTab(1)}
              >
                Sign Up
              </button>
            </div>

            <AnimatePresence exitBeforeEnter>
              <motion.div
                key={tab}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {tab === 0 ? <Login /> : <Signup />}
              </motion.div>
            </AnimatePresence>

            <div className="secure-note">
              🔒 <span><b>256-bit encrypted.</b> Your secrets stay secret.</span>
            </div>
          </div>
        </motion.div>
        </div>
      </div>

      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          {[0, 1].map((dup) => (
            <span key={dup} style={{ display: "inline-flex", gap: "44px" }}>
              <span><em>⚡</em>Sub-50ms delivery</span>
              <span><em>✦</em>Group rooms</span>
              <span><em>✦</em>Instant search</span>
              <span><em>✦</em>Dark-native</span>
              <span><em>✦</em>Free forever</span>
              <span><em>✦</em>Zero boring chats</span>
            </span>
          ))}
        </div>
      </div>

      <footer className="auth-footer">
        <Text fontSize="xs" opacity={0.85}>
          Crafted with obsession by{" "}
          <a href="https://istiaque-portfolio.netlify.app" target="_blank" rel="noopener noreferrer">
            Istiaque Ahmed Nuhash
          </a>{" "}
          © {new Date().getFullYear()} — Meet-Up. Talk fast. Vibe harder.
        </Text>
      </footer>
    </Box>
    </MotionConfig>
  );
}

export default Homepage;
